import React, { useState, useEffect } from 'react';
import { Button, Text, Group, Textarea, Box, Title, ThemeIcon, Progress, Stack, Avatar, Paper, Modal, TextInput, NumberInput } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconStar, IconStarFilled, IconUser, IconLock } from '@tabler/icons-react';
import { getReviews, addReview } from '../../api';
import { toast } from 'react-toastify';

const ReviewSection = ({ productId }) => {
  const [reviews, setReviews] = useState([]);
  const [opened, { open, close }] = useDisclosure(false);
  
  // Form State
  const [formData, setFormData] = useState({
    user: '',
    title: '',
    comment: '',
    rating: 0
  });

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReviews();
  }, [productId]);

  const fetchReviews = async () => {
    try {
      const { data } = await getReviews(productId);
      if (Array.isArray(data)) {
        setReviews(data);
      } else {
        setReviews([]);
      }
    } catch (error) {
      console.error("Failed to fetch reviews", error);
      setReviews([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    const { user, title, comment, rating } = formData;
    
    if (!user || !title || !comment || rating === 0) {
      toast.error("Please fill all fields and provide a rating.");
      return;
    }

    try {
      await addReview({ ...formData, productId });
      toast.success("Review submitted!");
      setFormData({ user: '', title: '', comment: '', rating: 0 });
      close();
      fetchReviews();
    } catch (error) {
      toast.error("Failed to submit review.");
    }
  };

  const averageRating = Array.isArray(reviews) && reviews.length ? (reviews.reduce((a, b) => a + b.rating, 0) / reviews.length).toFixed(1) : 0;

  return (
    <Box mt="xl" p="xl" style={{ background: 'rgba(255,255,255,0.05)', backdropFilter: 'blur(10px)', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.1)' }}>
      <Group position="apart" mb="xl">
          <div>
            <Title order={2} color="white">Customer Reviews</Title>
            <Group align="flex-end" spacing="xs">
                <Text size="3rem" weight={700} color="white" style={{ lineHeight: 1 }}>{averageRating}</Text>
                <Box mb={6}>
                    <Group spacing={4}>
                        {[1, 2, 3, 4, 5].map((star) => (
                        <ThemeIcon key={star} variant="transparent" color={star <= Math.round(averageRating) ? "yellow" : "gray"}>
                            <IconStarFilled size="1rem" />
                        </ThemeIcon>
                        ))}
                    </Group>
                    <Text color="dimmed" size="xs">{reviews.length} Reviews</Text>
                </Box>
            </Group>
          </div>
          <Button variant="gradient" gradient={{ from: 'cyan', to: 'indigo' }} size="md" onClick={open}>Write a Review</Button>
      </Group>

      <Modal 
        opened={opened} 
        onClose={close} 
        title="Write a Review" 
        centered 
        styles={{ 
          content: { backgroundColor: '#1a1b1e', color: 'white', border: '1px solid rgba(255,255,255,0.1)' }, 
          header: { backgroundColor: '#1a1b1e', color: 'white' },
          body: { backgroundColor: '#1a1b1e', color: 'white' },
          title: { color: 'white', fontWeight: 600 },
          close: { color: 'white', '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }
        }}
      >
         <Stack spacing="md">
            <Box>
                <Text size="sm" mb={4} color="dimmed">Overall Rating</Text>
                <Group>
                {[1, 2, 3, 4, 5].map((star) => (
                    <ThemeIcon 
                    key={star} 
                    variant="transparent" 
                    size="lg"
                    style={{ cursor: 'pointer' }}
                    onClick={() => setFormData({ ...formData, rating: star })}
                    >
                    <IconStarFilled size={28} color={star <= formData.rating ? "#fcc419" : "gray"} />
                    </ThemeIcon>
                ))}
                </Group>
            </Box>

            <TextInput 
                label="Name" 
                placeholder="Your Name" 
                required 
                value={formData.user}
                onChange={(e) => setFormData({ ...formData, user: e.target.value })}
                styles={{ 
                  input: { backgroundColor: 'rgba(255,255,255,0.05)', color: 'white', border: '1px solid rgba(255,255,255,0.1)' }, 
                  label: { color: 'white' } 
                }}
            />

            <TextInput 
                label="Review Title" 
                placeholder="Sum up your experience" 
                required 
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                styles={{ 
                  input: { backgroundColor: 'rgba(255,255,255,0.05)', color: 'white', border: '1px solid rgba(255,255,255,0.1)' }, 
                  label: { color: 'white' } 
                }} 
            />

            <Textarea 
                label="Review" 
                placeholder="Tell us what you liked or disliked..." 
                minRows={3} 
                required
                value={formData.comment}
                onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
                styles={{ 
                  input: { backgroundColor: 'rgba(255,255,255,0.05)', color: 'white', border: '1px solid rgba(255,255,255,0.1)' }, 
                  label: { color: 'white' } 
                }}
            />

            <Button fullWidth variant="gradient" gradient={{ from: 'cyan', to: 'indigo' }} mt="md" onClick={handleSubmit}>Submit Review</Button>
         </Stack>
      </Modal>

      <Stack spacing="md" mt="xl">
        {reviews.map((review) => (
          <Paper key={review._id} p="md" style={{ background: 'transparent', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
            <Group position="apart" mb="xs">
              <Group>
                <Avatar radius="xl" color="cyan" size="md">{review.user.charAt(0)}</Avatar>
                <div>
                  <Text color="white" size="sm" weight={600}>{review.user}</Text>
                  <Group spacing={4}>
                     <Group spacing={2}>
                        {[...Array(review.rating)].map((_, i) => <IconStarFilled key={i} size={14} color="#fcc419" />)}
                    </Group>
                    {review.title && <Text color="white" size="sm" weight={700}>- {review.title}</Text>}
                  </Group>
                </div>
              </Group>
              <Text color="dimmed" size="xs">{new Date(review.createdAt).toLocaleDateString()}</Text>
            </Group>
            <Text color="gray.3" size="sm" style={{ paddingLeft: 50 }}>{review.comment}</Text>
          </Paper>
        ))}
      </Stack>
    </Box>
  );
};

export default ReviewSection;
